import React, { useState, useEffect } from 'react';
import api from "../utils/api";
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  DollarSign, 
  Calendar, 
  Users, 
  Download,
  TrendingUp,
  FileText,
  ArrowLeft,
  Printer,
  AlertCircle,
  Edit,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from './LoadingSpinner';
import DashboardPageLayout from './DashboardPageLayout';
import PageGuide from './PageGuide';
import { GUIDE_CONTENT } from '../config/guideContent';
import { PrimaryButton, SecondaryButton } from './Buttons';
import FormSection from './FormSection';
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const PayrollTab = () => {
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [payrollData, setPayrollData] = useState(null);
  const [payRates, setPayRates] = useState({});
  const [editingPayRates, setEditingPayRates] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [includeTipPool, setIncludeTipPool] = useState(false); // Default OFF
  const [teamMembersList, setTeamMembersList] = useState([]);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  useEffect(() => {
    // Set default dates (current week)
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    setStartDate(monday.toISOString().split('T')[0]);
    setEndDate(sunday.toISOString().split('T')[0]);
    
    fetchTeamMembers();
    fetchPayRates();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get(`/team`);
      setTeamMembersList(response.data || []);
    } catch (error) {
      console.error('Error fetching teamMembers:', error);
    }
  };

  const fetchPayRates = async () => {
    try {
      const response = await api.get(`/payroll/pay-rates`);
      const rates = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(r => {
          rates[r.teamMemberId] = r.payRate || 20;
        });
      } else if (response.data.payRates) {
        Object.assign(rates, response.data.payRates);
      }
      setPayRates(rates);
    } catch (error) {
      console.error('Error fetching pay rates:', error);
    }
  };

  const updatePayRate = async (teamMemberId, newRate) => {
    try {
      await api.put(`/payroll/pay-rate/${teamMemberId}`, {
        payRate: parseFloat(newRate) || 0
      });
      setPayRates(prev => ({ ...prev, [teamMemberId]: parseFloat(newRate) || 0 }));
      toast.success('Pay rate updated');
    } catch (error) {
      console.error('Error updating pay rate:', error);
      toast.error('Failed to update pay rate');
    }
  };

  const generatePayroll = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    setLoading(true);
    try {
      console.log('Generating payroll with:', {
        startDate,
        endDate,
        payRates,
        teamMembersCount: teamMembersList.length
      });
      
      const response = await api.post(`/payroll/generate`, {
        startDate,
        endDate,
        payRates
      });
      
      console.log('Payroll response:', response.data);
      
      const data = response.data;
      setPayrollData(data);
      
      // Log the results
      console.log('Payroll data received:', {
        totalTeamMembers: data.teamMembers?.length || 0,
        totalHours: data.totalHours || 0,
        totalPayroll: data.totalPayroll || 0,
        teamMemberDetails: data.teamMembers?.map(c => ({
          name: c.teamMemberName,
          hours: c.hoursWorked,
          earnings: c.totalEarnings
        }))
      });
      
      toast.success('Payroll report generated successfully!');
    } catch (error) {
      console.error('Error generating payroll:', error);
      console.error('Error details:', error.response?.data);
      
      // Handle insufficient actions error (402) with proper modal
      if (error.response?.status === 402) {
        const detail = error.response.data.detail;
        toast.error(`Not enough automation actions. Need ${detail.required}, have ${detail.balance}. Please purchase more actions.`);
      } else {
        // Handle other errors
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to generate payroll report';
        if (typeof errorMsg === 'object') {
          toast.error('Failed to generate payroll report - see console for details');
        } else {
          toast.error(errorMsg);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const printTeamMemberReport = (teamMember) => {
    console.log('Printing report for teamMember:', teamMember);
    const printWindow = window.open('', '', 'width=800,height=600');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payroll Report - ${teamMember.teamMemberName || 'Unknown'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1e40af; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background-color: #1e40af; color: white; }
          tr:nth-child(even) { background-color: #f9fafb; }
          .summary { background-color: #eff6ff; padding: 15px; border-radius: 8px; margin: 20px 0; }
          .tip-pool { background-color: #f3e8ff; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #9333ea; }
          .disclaimer { background-color: #fef3c7; padding: 15px; border-left: 4px solid #f59e0b; margin-top: 20px; font-size: 12px; }
          .total { font-weight: bold; font-size: 18px; }
          .percentage { color: #9333ea; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>CharterShare - Payroll Report</h1>
        <h2>${teamMember.teamMemberName || 'Unknown'}</h2>
        <p><strong>Period:</strong> ${startDate} to ${endDate}</p>
        
        <div class="summary">
          <h3>Summary</h3>
          <p><strong>Total Hours:</strong> ${(teamMember.totalHours || teamMember.hoursWorked || 0).toFixed(2)} hours</p>
          <p><strong>Pay Rate:</strong> $${(teamMember.payRate || 0).toFixed(2)}/hour</p>
          <p><strong>Total Pay:</strong> $${(teamMember.totalPay || teamMember.totalEarnings || 0).toFixed(2)}</p>
        </div>
        
        <h3>Booking Details</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Asset</th>
              <th>Time</th>
              <th>Duration</th>
              <th>Hours</th>
              <th>Pay</th>
              ${includeTipPool ? '<th>Tip Pool Share %</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${(teamMember.trips || []).map(trip => {
              let tipInfo = '';
              if (includeTipPool && teamMember.dailyTipShares && teamMember.dailyTipShares[trip.date]) {
                const dayTip = teamMember.dailyTipShares[trip.date];
                tipInfo = `<td class="percentage">${(dayTip.percentage || 0).toFixed(1)}%</td>`;
              } else if (includeTipPool) {
                tipInfo = '<td>0.0%</td>';
              }
              
              return `
              <tr>
                <td>${trip.date || 'Unknown'}</td>
                <td>${trip.assetName || 'Unknown'}</td>
                <td>${trip.startTime || ''} - ${trip.endTime || ''}</td>
                <td>${trip.duration || 0} min</td>
                <td>${(trip.hours || 0).toFixed(2)}</td>
                <td>$${(trip.pay || 0).toFixed(2)}</td>
                ${tipInfo}
              </tr>
            `}).join('')}
          </tbody>
        </table>
        
        ${includeTipPool && teamMember.dailyTipShares ? `
        <div class="tip-pool">
          <h3>💰 Daily Tip Pool</h3>
          <p><em>Your percentage of the daily tip pool, based on hours worked</em></p>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Your Hours</th>
                <th>Team Total Hours</th>
                <th>Your Share of Team Tips %</th>
              </tr>
            </thead>
            <tbody>
              ${Object.entries(teamMember.dailyTipShares || {}).map(([date, dayTip]) => `
                <tr>
                  <td>${date}</td>
                  <td>${(dayTip?.hoursWorked || 0).toFixed(2)}</td>
                  <td>${(dayTip?.dayTotalHours || 0).toFixed(2)}</td>
                  <td class="percentage"><strong>${(dayTip?.percentage || 0).toFixed(1)}%</strong></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
        ` : ''}
        
        <div class="disclaimer">
          <h4>⚠️ DISCLAIMER</h4>
          <p>This payroll report is generated by CharterShare as a calculation tool. While we strive for accuracy, this report should be used as a reference only. Please verify all calculations and ensure compliance with your local labor laws, tax regulations, and company policies. CharterShare is not responsible for any payroll errors or discrepancies. Always perform your own due diligence before processing payments.</p>
        </div>
        
        <p style="margin-top: 30px; font-size: 12px; color: #666;">Generated on ${new Date().toLocaleString()}</p>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadTeamMemberCSV = (teamMember) => {
    console.log('Downloading CSV for teamMember:', teamMember);
    
    let csvContent = `CharterShare - Payroll Report\n`;
    csvContent += `TeamMember: ${teamMember.teamMemberName || 'Unknown'}\n`;
    csvContent += `Period: ${startDate} to ${endDate}\n\n`;
    
    csvContent += `TEAM MEMBER: ${teamMember.teamMemberName}\n`;
    csvContent += `Pay Rate,$${(teamMember.payRate || 0).toFixed(2)}/hr\n`;
    csvContent += `Total Hours,${(teamMember.totalHours || teamMember.hoursWorked || 0).toFixed(2)}\n`;
    csvContent += `Base Pay,$${(teamMember.totalPay || teamMember.totalEarnings || 0).toFixed(2)}\n`;
    csvContent += `Total Pay,$${(teamMember.totalPay || teamMember.totalEarnings || 0).toFixed(2)}\n`;
    csvContent += '\n';
    
    csvContent += `TRIP DETAILS\n`;
    csvContent += `Date,Asset,Start Time,End Time,Duration (min),Hours,Pay`;
    if (includeTipPool) {
      csvContent += `,Tip Pool Share %`;
    }
    csvContent += '\n';
    
    // Safely handle trips array
    const trips = teamMember.trips || [];
    trips.forEach(trip => {
      csvContent += `${trip.date || 'Unknown'},${trip.assetName || 'Unknown'},${trip.startTime || ''},${trip.endTime || ''},${trip.duration || 0},${(trip.hours || 0).toFixed(2)},$${(trip.pay || 0).toFixed(2)}`;
      
      if (includeTipPool && teamMember.dailyTipShares) {
        const dayTip = teamMember.dailyTipShares[trip.date];
        if (dayTip && dayTip.percentage !== undefined) {
          csvContent += `,${dayTip.percentage.toFixed(1)}%`;
        } else {
          csvContent += `,0.0%`;
        }
      }
      csvContent += '\n';
    });
    
    // Safely handle daily tip shares
    if (includeTipPool && teamMember.dailyTipShares) {
      csvContent += '\nDAILY TIP POOL SHARE\n';
      csvContent += 'Date,Hours Worked,Team Total Hours,Tip Pool Share %\n';
      Object.entries(teamMember.dailyTipShares).forEach(([date, dayTip]) => {
        csvContent += `${date},${(dayTip.hoursWorked || 0).toFixed(2)},${(dayTip.dayTotalHours || 0).toFixed(2)},${(dayTip.percentage || 0).toFixed(1)}%\n`;
      });
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payroll_${teamMember.teamMemberName.replace(/\s+/g, '_')}_${startDate}_to_${endDate}.csv`;
    link.click();
    
    toast.success(`Downloaded ${teamMember.teamMemberName}'s payroll report`);
  };

  const downloadAllReports = () => {
    if (!payrollData?.teamMemberSummary) {
      toast.error('No payroll data available');
      return;
    }

    // Generate CSV with all teamMember data
    let csvContent = 'CharterShare - Payroll Report (All TeamMembers)\n';
    csvContent += `Period: ${startDate} to ${endDate}\n\n`;
    
    // Add summary
    csvContent += 'SUMMARY\n';
    csvContent += `Total TeamMembers,${payrollData.teamMemberSummary.length}\n`;
    csvContent += `Total Hours,${payrollData.totalHours?.toFixed(2) || '0.00'}\n`;
    csvContent += `Total Payroll,$${payrollData.totalPay?.toFixed(2) || '0.00'}\n`;
    csvContent += '\n';
    
    // Add teamMember details
    payrollData.teamMemberSummary.forEach(teamMember => {
      csvContent += `\nTEAM MEMBER: ${teamMember.teamMemberName}\n`;
      csvContent += `Pay Rate,$${teamMember.payRate?.toFixed(2) || '0.00'}/hr\n`;
      csvContent += `Total Hours,${teamMember.totalHours?.toFixed(2) || '0.00'}\n`;
      csvContent += `Total Pay,$${teamMember.totalPay?.toFixed(2) || '0.00'}\n`;
      
      csvContent += '\nDate,Asset,Start Time,End Time,Duration (min),Hours,Pay';
      if (includeTipPool) {
        csvContent += ',Tip Pool Share %';
      }
      csvContent += '\n';
      
      // Safely handle trips
      const trips = teamMember.trips || [];
      trips.forEach(trip => {
        csvContent += `${trip.date || 'Unknown'},${trip.assetName || 'Unknown'},${trip.startTime || ''},${trip.endTime || ''},${trip.duration || 0},${(trip.hours || 0).toFixed(2)},$${(trip.pay || 0).toFixed(2)}`;
        
        if (includeTipPool && teamMember.dailyTipShares) {
          const dayTip = teamMember.dailyTipShares[trip.date];
          if (dayTip && dayTip.percentage !== undefined) {
            csvContent += `,${dayTip.percentage.toFixed(1)}%`;
          } else {
            csvContent += `,0.0%`;
          }
        }
        csvContent += '\n';
      });
      csvContent += '\n';
    });
    
    // Add daily breakdown  
    if (includeTipPool && payrollData.dailyBreakdown) {
      csvContent += '\nDAILY TIP POOL BREAKDOWN\n';
      csvContent += 'Date,Total Team Hours,TeamMembers Working\n';
      payrollData.dailyBreakdown.forEach(day => {
        csvContent += `${day.date || 'Unknown'},${(day.totalHours || 0).toFixed(2)},${day.teamMemberCount || 0}\n`;
      });
    }
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payroll_all_teamMembers_${startDate}_to_${endDate}.csv`;
    link.click();
    
    toast.success('Payroll CSV downloaded successfully');
  };

  // TeamMember Detail View
  if (selectedTeamMember) {
    const teamMemberData = payrollData?.teamMemberSummary?.find(c => c.teamMemberId === selectedTeamMember.teamMemberId);
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setSelectedTeamMember(null)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Summary
            </Button>
            <h2 className="text-2xl font-bold">{selectedTeamMember.teamMemberName}</h2>
          </div>
          <Button
            onClick={() => printTeamMemberReport(selectedTeamMember)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
        </div>

        {/* Disclaimer */}
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">Payroll Calculator Disclaimer</h4>
                <p className="text-sm text-yellow-800">
                  This is a calculation tool provided for reference purposes. Please verify all amounts and ensure compliance with applicable labor laws and tax regulations before processing any payments. CharterShare is not liable for payroll errors or discrepancies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-none border-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{selectedTeamMember.totalHours.toFixed(2)}</div>
              <p className="text-xs text-gray-500">{selectedTeamMember.trips.length} trips</p>
            </CardContent>
          </Card>

          <Card className="shadow-none border-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Pay Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${selectedTeamMember.payRate.toFixed(2)}</div>
              <p className="text-xs text-gray-500">per hour</p>
            </CardContent>
          </Card>

          <Card className="shadow-none border-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Base Pay</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">${selectedTeamMember.totalPay.toFixed(2)}</div>
              <p className="text-xs text-gray-500">hours × rate</p>
            </CardContent>
          </Card>

          {includeTipPool && teamMemberData && (
            <Card className="shadow-none border-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Tip Pool Share</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">${teamMemberData.tipShare?.toFixed(2) || '0.00'}</div>
                <p className="text-xs text-gray-500">{teamMemberData.tipPercentage?.toFixed(1) || '0'}% of pool</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Booking Details Table */}
        <Card>
          <CardHeader>
            <CardTitle>Booking Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Asset</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Time</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Hours</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700">Pay</th>
                    {includeTipPool && <th className="text-right py-3 px-4 font-semibold text-gray-700">Tips</th>}
                  </tr>
                </thead>
                <tbody>
                  {selectedTeamMember.trips.map((trip, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">{trip.date}</td>
                      <td className="py-3 px-4">{trip.assetName}</td>
                      <td className="py-3 px-4 text-sm">{trip.startTime} - {trip.endTime}</td>
                      <td className="py-3 px-4 text-right font-medium">{trip.hours.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-medium text-green-600">
                        ${(trip.hours * selectedTeamMember.payRate).toFixed(2)}
                      </td>
                      {includeTipPool && (
                        <td className="py-3 px-4 text-right font-medium text-purple-600">
                          ${trip.tips?.toFixed(2) || '0.00'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <DashboardPageLayout
      title="Payroll Calculator"
      subtitle="Follow the steps below to generate your payroll report"
      guideIcon={GUIDE_CONTENT.payroll.icon}
      guideTitle={GUIDE_CONTENT.payroll.title}
      guideDescription={GUIDE_CONTENT.payroll.description}
      actions={
        <PrimaryButton
        onClick={() => setShowDisclaimerModal(true)}
        disabled={loading || !startDate || !endDate}
        loading={loading}
        size="lg"
        className="text-base px-6"
      >
        <FileText className="w-5 h-5 mr-2" />
        Generate Payroll Report
      </PrimaryButton>
      }
    >
      <div className="space-y-6">
        {/* STEP 1: Date Range Selection */}
        <FormSection
          stepNumber={1}
          title="Select Date Range"
          description="Choose the start and end dates for your payroll period"
          color="#3B82F6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Start Date *</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11 text-base"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">End Date *</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-11 text-base"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2 bg-gray-50 p-3 rounded-lg">
              <Checkbox
                id="includeTipPool"
                checked={includeTipPool}
                onCheckedChange={setIncludeTipPool}
              />
              <label
                htmlFor="includeTipPool"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Include tip pool calculations in report
              </label>
            </div>
        </FormSection>

        {/* STEP 2: TeamMember Pay Rates */}
        <FormSection
          stepNumber={2}
          title="Review & Update TeamMember Pay Rates"
          description={`${teamMembersList.length} teamMember${teamMembersList.length !== 1 ? 's' : ''} with hourly rates configured`}
          color="#9333EA"
          collapsible={false}
          expanded={true}
        >
          {/* Edit Rates Button */}
          <div className="flex justify-end mb-4">
            <Button
              variant={editingPayRates ? "default" : "outline"}
              size="sm"
              onClick={() => setEditingPayRates(!editingPayRates)}
              className={editingPayRates ? "bg-purple-600 hover:bg-purple-700" : ""}
            >
              {editingPayRates ? (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Done Editing
                </>
              ) : (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Rates
                </>
              )}
            </Button>
          </div>
          {editingPayRates && (
            <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <p className="font-semibold mb-1">Editing Mode Active</p>
                <p>Update hourly rates for each teamMember. Changes save automatically when you click outside the input field.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamMembersList.map(teamMember => (
                  <div 
                    key={teamMember.id} 
                    className={`flex items-center justify-between p-4 border-2 rounded-lg transition-all ${
                      editingPayRates 
                        ? 'border-purple-300 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-200'
                    }`}
                  >
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{teamMember.name}</p>
                      <p className="text-xs text-gray-500">{teamMember.username}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-lg font-semibold text-gray-700">$</span>
                      <Input
                        type="number"
                        value={payRates[teamMember.id] || 20}
                        onChange={(e) => setPayRates(prev => ({ ...prev, [teamMember.id]: e.target.value }))}
                        onBlur={(e) => editingPayRates && updatePayRate(teamMember.id, e.target.value)}
                        disabled={!editingPayRates}
                        className={`w-20 text-right text-lg font-semibold ${
                          editingPayRates ? 'border-purple-400 bg-white' : 'bg-gray-50 border-gray-300'
                        }`}
                        step="0.50"
                        min="0"
                      />
                      <span className="text-sm text-gray-600">/hr</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {teamMembersList.length === 0 && (
                <div className="text-center text-gray-500 py-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p className="font-medium">No teamMembers found</p>
                  <p className="text-sm mt-1">Add teamMembers to your organization to set pay rates</p>
                </div>
              )}

        </FormSection>

        {/* STEP 3: Generate Report */}
        <FormSection
          stepNumber={3}
          title="Generate Payroll Report"
          description={!startDate || !endDate 
            ? 'Complete Step 1 to enable report generation'
            : 'Ready to generate! Click the button above to create your report'
          }
          color="#10B981"
        >
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <p className="text-sm text-gray-700 mb-2">
                  Your payroll report will include:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Hours worked by each teamMember
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Total earnings calculation
                  </li>
                  {includeTipPool && (
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Tip pool distribution
                    </li>
                  )}
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    Downloadable CSV export
                  </li>
                </ul>
              </div>
            </div>
        </FormSection>
      </div>

      {/* Results */}
      {payrollData && (
        <div className="space-y-6 mt-8 pt-8 border-t-4 border-blue-500">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total TeamMembers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{payrollData.teamMemberSummary?.length || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Hours</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{payrollData.totalHours?.toFixed(2) || '0.00'}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Payroll</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">${payrollData.totalPay?.toFixed(2) || '0.00'}</div>
              </CardContent>
            </Card>
          </div>

          {/* TeamMember List */}
          <Card className="shadow-none border-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>TeamMember Payroll Summary</CardTitle>
                <Button
                  onClick={downloadAllReports}
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download All Reports
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {payrollData.teamMemberSummary?.map((teamMember) => {
                  // Calculate tip pool percentage
                  const totalHours = payrollData.totalHours || 0;
                  const tipPercentage = totalHours > 0 ? ((teamMember.totalHours / totalHours) * 100) : 0;
                  
                  return (
                    <div
                      key={teamMember.teamMemberId}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
                      onClick={() => setSelectedTeamMember(teamMember)}
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{teamMember.teamMemberName}</h3>
                        <div className="flex gap-4 mt-1 text-sm text-gray-600">
                          <span>{teamMember.totalHours?.toFixed(2) || '0.00'} hours</span>
                          <span>•</span>
                          <span>{teamMember.trips?.length || 0} trips</span>
                          <span>•</span>
                          <span>${teamMember.payRate?.toFixed(2) || '0.00'}/hr</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-3">
                          <div className="text-lg font-bold text-green-600">${teamMember.totalPay?.toFixed(2) || '0.00'}</div>
                        </div>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadTeamMemberCSV(teamMember);
                          }}
                          variant="ghost"
                          size="sm"
                          title="Download CSV"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            printTeamMemberReport(teamMember);
                          }}
                          variant="ghost"
                          size="sm"
                          title="Print Report"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                }) || <p className="text-center text-gray-500 py-4">No payroll data</p>}
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="border-yellow-200 bg-yellow-50 shadow-none">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-2">Important Notice</h4>
                  <p className="text-sm text-yellow-800">
                    This payroll calculator is provided as a convenience tool. While we strive for accuracy, BookingFlows is not responsible for any calculation errors or discrepancies. Please verify all figures and ensure compliance with applicable labor laws, tax regulations, and company policies before processing payments. Always perform your own due diligence.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-yellow-100 p-3 rounded-lg flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Rough Payroll Calculator
                </h3>
                <p className="text-sm text-gray-700">
                  This is a simple tool to estimate payroll costs. <strong>Please do your own due diligence</strong> and consult with a payroll professional or accountant for accurate calculations, tax compliance, and legal requirements.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDisclaimerModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowDisclaimerModal(false);
                  generatePayroll();
                }}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardPageLayout>
  );
};

export default PayrollTab;
